import { Modal, Form, Input, InputNumber, Upload, Button, message, Select, Typography, Table, Tag } from 'antd';
import { UploadOutlined, CloseOutlined, FilePdfOutlined, PictureOutlined, DollarOutlined, TagOutlined, InboxOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { Product, useProducts, CreateProductInput, UpdateProductInput, ProductVariant, useAuth } from '@hooks';
import type { UploadFile } from 'antd/es/upload/interface';
import { PrimaryButton } from '@components/atoms';

const { TextArea } = Input;
const { Option } = Select;
const { Title } = Typography;

interface ProductFormModalProps {
  open: boolean;
  onCancel: () => void;
  editingProduct: Product | null;
  onSuccess: () => void;
}

interface DocumentWithMetadata {
  file: File;
  name: string;
  description: string;
  existingDocId?: string; // ID of existing document (if editing)
  existingDocUrl?: string; // URL of existing document (if editing)
}

interface VariantFormData {
  sku: string;
  size: string;
  price: number;
  stockQuantity: number;
}

const PREDEFINED_CATEGORIES = [
  'Apparel',
  'Accessories',
  'Equipment',
  'Tools',
  'Parts',
  'Other'
];

// Styled section wrapper
const FormSection = ({ title, icon, children }: { title: string; icon?: React.ReactNode; children: React.ReactNode }) => (
  <div className="mb-6">
    <div className="flex items-center gap-2 mb-4">
      {icon && <span className="text-yellow-principal text-lg">{icon}</span>}
      <Title level={5} className="font-android !font-normal !text-white !mb-0 uppercase tracking-wider">
        {title}
      </Title>
    </div>
    <div className="border-2 border-zinc-700 p-5 bg-zinc-900/50">
      {children}
    </div>
  </div>
);

export const ProductFormModal = ({ open, onCancel, editingProduct, onSuccess }: ProductFormModalProps) => {
  const [form] = Form.useForm();
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [documentFiles, setDocumentFiles] = useState<DocumentWithMetadata[]>([]);
  const [variants, setVariants] = useState<VariantFormData[]>([]);
  const [sizes, setSizes] = useState<string[]>([]);
  const [newSize, setNewSize] = useState('');
  const { addProduct, updateProduct } = useProducts();
  const { isAuthenticated, profile } = useAuth();

  const isEditing = !!editingProduct;

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      if (editingProduct) {
        form.setFieldsValue({
          name: editingProduct.name,
          description: editingProduct.description,
          imageUrl: editingProduct.imageUrl,
          videoUrl: editingProduct.videoUrl,
          category: editingProduct.category,
        });
        
        // Set image previews if editing and product has images
        if (editingProduct.images && editingProduct.images.length > 0) {
          const bucketName = import.meta.env.VITE_AWS_S3_BUCKET || 'ruppert-production';
          const region = import.meta.env.VITE_AWS_S3_BUCKET_REGION || 'us-east-2';
          const previews = editingProduct.images.map(img => 
            img.startsWith('http') ? img : `https://${bucketName}.s3.${region}.amazonaws.com/${img}`
          );
          setImagePreviews(previews);
        } else if (editingProduct.imageUrl) {
          // Fallback to legacy imageUrl
          const bucketName = import.meta.env.VITE_AWS_S3_BUCKET || 'ruppert-production';
          const region = import.meta.env.VITE_AWS_S3_BUCKET_REGION || 'us-east-2';
          const fullImageUrl = editingProduct.imageUrl.startsWith('http') 
            ? editingProduct.imageUrl 
            : `https://${bucketName}.s3.${region}.amazonaws.com/${editingProduct.imageUrl}`;
          setImagePreviews([fullImageUrl]);
        }

        // Set variants if editing
        if (editingProduct.variants && editingProduct.variants.length > 0) {
          setVariants(editingProduct.variants.map((v: ProductVariant) => ({
            sku: v.sku,
            size: v.size,
            price: Number(v.price),
            stockQuantity: v.stockQuantity,
          })));
          
          // Extract unique sizes
          const uniqueSizes = [...new Set(editingProduct.variants.map((v: ProductVariant) => v.size))];
          setSizes(uniqueSizes);
        } else {
          setVariants([]);
          setSizes([]);
        }

        // Load existing documents if editing and product has documents
        if (editingProduct.documents && editingProduct.documents.length > 0) {
          // Convert existing documents to DocumentWithMetadata format
          const existingDocs = editingProduct.documents.map(doc => ({
            file: new File([], doc.fileUrl, { type: 'application/pdf' }), // Placeholder file with URL as name
            name: doc.name,
            description: doc.description || '',
            existingDocId: doc.id, // Mark as existing document
            existingDocUrl: doc.fileUrl, // Store the URL for display
          }));
          setDocumentFiles(existingDocs as any);
        } else {
          setDocumentFiles([]);
        }
      } else {
        // Reset with default values for new product
        form.setFieldsValue({
          name: '',
          description: '',
          imageUrl: '',
          videoUrl: '',
          category: '',
        });
        setImagePreviews([]);
        setVariants([]);
        setSizes([]);
        setDocumentFiles([]);
      }
      setImageFiles([]);
      setNewSize('');
    }
  }, [open, editingProduct, form]);

  const handleImagesChange = (info: any) => {
    const files = info.fileList.map((file: UploadFile) => file.originFileObj).filter(Boolean) as File[];
    
    if (files.length > 5) {
      message.error('You can only upload up to 5 images!');
      return;
    }

    // Validate file types
    const invalidFiles = files.filter(file => !file.type.startsWith('image/'));
    if (invalidFiles.length > 0) {
      message.error('You can only upload image files!');
      return;
    }

    // Validate file sizes (5MB each)
    const largeFiles = files.filter(file => file.size / 1024 / 1024 > 5);
    if (largeFiles.length > 0) {
      message.error('Each image must be smaller than 5MB!');
      return;
    }

    setImageFiles(files);
    
    // Create previews
    const previews: string[] = [];
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        previews.push(e.target?.result as string);
        if (previews.length === files.length) {
          setImagePreviews(previews);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleDocumentAdd = () => {
    if (documentFiles.length >= 3) {
      message.error('You can only add up to 3 documents!');
      return;
    }
    
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf';
    input.onchange = (e: any) => {
      const file = e.target.files[0];
      if (file) {
        if (file.type !== 'application/pdf') {
          message.error('Only PDF files are allowed!');
          return;
        }
        if (file.size / 1024 / 1024 > 10) {
          message.error('PDF file must be smaller than 10MB!');
          return;
        }
        
        setDocumentFiles(prev => [...prev, {
          file,
          name: file.name.replace('.pdf', ''),
          description: ''
        }]);
      }
    };
    input.click();
  };

  const handleDocumentRemove = (index: number) => {
    setDocumentFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDocumentMetadataChange = (index: number, field: 'name' | 'description', value: string) => {
    setDocumentFiles(prev => prev.map((doc, i) => 
      i === index ? { ...doc, [field]: value } : doc
    ));
  };

  // Variant management functions
  const handleAddSize = () => {
    if (newSize.trim() && !sizes.includes(newSize.trim())) {
      setSizes(prev => [...prev, newSize.trim()]);
      setNewSize('');
    }
  };

  const handleRemoveSize = (size: string) => {
    setSizes(prev => prev.filter(s => s !== size));
    // Remove variants with this size
    setVariants(prev => prev.filter(v => v.size !== size));
  };

  const handleAddVariant = () => {
    if (sizes.length === 0) {
      message.warning('Please add at least one size first');
      return;
    }
    
    // Find first size that doesn't have a variant
    for (const size of sizes) {
      const exists = variants.some(v => v.size === size);
      if (!exists) {
        setVariants(prev => [...prev, {
          sku: '',
          size,
          price: 0,
          stockQuantity: 0,
        }]);
        return;
      }
    }
    
    message.info('All sizes already have variants');
  };

  const handleRemoveVariant = (index: number) => {
    setVariants(prev => prev.filter((_, i) => i !== index));
  };

  const handleVariantChange = (index: number, field: keyof VariantFormData, value: any) => {
    setVariants(prev => prev.map((variant, i) => 
      i === index ? { ...variant, [field]: value } : variant
    ));
  };

  const generateAllVariants = () => {
    if (sizes.length === 0) {
      message.warning('Please add sizes first');
      return;
    }

    const newVariants: VariantFormData[] = [];
    
    for (const size of sizes) {
      const existing = variants.find(v => v.size === size);
      newVariants.push(existing || {
        sku: '',
        size,
        price: 0,
        stockQuantity: 0,
      });
    }
    
    setVariants(newVariants);
    message.success(`Generated ${newVariants.length} variants`);
  };

  const handleSubmit = async (values: any) => {
    try {
      // Check if user is authenticated
      if (!isAuthenticated) {
        message.error('You must be logged in to create or update products');
        return;
      }

      // Wait for profile to load if it's still loading
      if (profile.isLoading) {
        message.warning('Waiting for profile to load...');
        return;
      }

      // Check if user is admin
      console.log('Profile data:', profile.data);
      console.log('Is authenticated:', isAuthenticated);
      console.log('Profile:', profile.data?.profile);
      console.log('Token:', localStorage.getItem('token'));
      
      if (!profile.data?.profile || profile.data.profile !== 'admin') {
        message.error('Administrator access required. Your current profile: ' + (profile.data?.profile || 'not defined'));
        console.error('User profile is not admin:', profile.data?.profile);
        return;
      }

      // Validate that at least one size exists
      if (sizes.length === 0) {
        message.error('Please add at least one size');
        return;
      }

      // Validate that all sizes have variants with prices
      if (variants.length === 0) {
        message.error('Please generate variants and set prices. At least 1 variant is required.');
        return;
      }

      // Validate that all variants have required fields
      const invalidVariants = variants.filter(v => !v.sku || !v.size || v.price <= 0);
      if (invalidVariants.length > 0) {
        message.error('All variants must have valid SKU, size, and price');
        return;
      }

      const processedVariants = Array.isArray(variants) && variants.length > 0 ? variants.map(v => ({
        sku: v.sku || '',
        size: v.size || '',
        price: Number(v.price) || 0,
        stockQuantity: Number(v.stockQuantity) || 0,
      })) : [];

      console.log('Variants being sent:', processedVariants);
      console.log('Variants length:', processedVariants.length);

      const productData: CreateProductInput | UpdateProductInput = {
        name: values.name,
        description: values.description,
        videoUrl: values.videoUrl || '',
        category: values.category,
        // Não enviar o campo 'sizes' - o backend não espera este campo
        variants: processedVariants,
      };

      console.log('Complete product data being sent:', productData);

      // Add images if uploaded
      if (imageFiles.length > 0) {
        productData.images = imageFiles;
      }

      // Add documents if uploaded
      if (documentFiles.length > 0) {
        productData.documents = documentFiles.map(doc => doc.file);
        productData.documentsMetadata = documentFiles.map(doc => ({
          name: doc.name,
          description: doc.description
        }));
      }

      if (isEditing && editingProduct) {
        await updateProduct.mutateAsync({
          id: editingProduct.id,
          data: productData as UpdateProductInput,
        });
      } else {
        await addProduct.mutateAsync(productData as CreateProductInput);
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving product:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setImageFiles([]);
    setImagePreviews([]);
    setDocumentFiles([]);
    setVariants([]);
    setSizes([]);
    setNewSize('');
    onCancel();
  };

  return (
    <Modal
      title={null}
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={800}
      closable={false}
      styles={{
        mask: { backdropFilter: 'blur(10px)', backgroundColor: 'rgba(0,0,0,0.8)' },
        content: { 
          backgroundColor: 'transparent', 
          boxShadow: 'none',
          padding: 0,
        },
      }}
      className="product-form-modal"
    >
      {/* Custom modal container */}
      <div className="bg-first-dark-gray border-2 border-zinc-600">

        {/* Header */}
        <div className="relative border-b border-zinc-700 px-8 py-6 flex items-center justify-between bg-second-black-carbon">
          <Title level={3} className="font-android !font-normal !text-white !mb-0 uppercase tracking-wider">
            {isEditing ? 'EDIT PRODUCT' : 'ADD NEW PRODUCT'}
          </Title>
          <Button
            type="text"
            icon={<CloseOutlined className="text-xl" />}
            onClick={handleCancel}
            className="!text-gray-400 hover:!text-white transition-colors"
          />
        </div>

        {/* Form Content */}
        <div className="px-8 py-6 max-h-[70vh] overflow-y-auto">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            requiredMark={false}
          >
            {/* Basic Information Section */}
            <FormSection title="Basic Information" icon={<TagOutlined />}>
              <Form.Item
                label={<span className="font-android text-white text-xs uppercase tracking-wider">Product Name</span>}
                name="name"
                rules={[
                  { required: true, message: 'Please enter product name' },
                  { min: 1, max: 255, message: 'Name must be between 1 and 255 characters' }
                ]}
                className="mb-0"
              >
                <Input 
                  className="!bg-zinc-800 font-barlow !border-none !text-white !rounded-none" 
                  placeholder="Enter product name"
                />
              </Form.Item>

              <Form.Item
                label={<span className="font-android text-white text-xs uppercase tracking-wider mt-4 block">Description</span>}
                name="description"
                rules={[
                  { required: true, message: 'Please enter product description' },
                  { max: 5000, message: 'Description must be less than 5000 characters' }
                ]}
                className="mb-0 mt-4"
              >
                <TextArea
                  rows={4}
                  className="!bg-zinc-800 font-barlow !border-none !text-white !rounded-none"
                  placeholder="Enter product description"
                />
              </Form.Item>
            </FormSection>

            {/* Category Section */}
            <FormSection title="Category" icon={<TagOutlined />}>
              <Form.Item
                label={<span className="font-android text-white text-xs uppercase tracking-wider">Category</span>}
                name="category"
                className="mb-0"
              >
                <Select 
                  className="font-barlow product-category-select !border-none"
                  placeholder="Select a category"
                  allowClear
                >
                  {PREDEFINED_CATEGORIES.map(cat => (
                    <Option key={cat} value={cat}>{cat}</Option>
                  ))}
                </Select>
              </Form.Item>
            </FormSection>

            {/* Sizes & Variants Section */}
            <FormSection title="Sizes & Pricing (Required)" icon={<DollarOutlined />}>
              <p className="font-barlow text-zinc-400 text-sm mb-4">
                Add sizes for this product. Price and stock are defined per size variant.
              </p>

              {/* Sizes */}
              <div className="mb-4">
                <label className="font-android text-white text-xs uppercase tracking-wider block mb-2">
                  SIZES <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newSize}
                    onChange={(e) => setNewSize(e.target.value)}
                    placeholder="e.g., S, M, L, XL"
                    className="!bg-zinc-800 font-barlow !border-none !text-white flex-1 !rounded-none"
                    onPressEnter={handleAddSize}
                  />
                  <Button
                    icon={<PlusOutlined />}
                    onClick={handleAddSize}
                    className="btn-yellow-principal font-android uppercase tracking-wider"
                    style={{ backgroundColor: '#E3AF25', borderColor: '#E3AF25', color: '#000000' }}
                  >
                    Add
                  </Button>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {sizes.map((size, index) => (
                    <Tag
                      key={index}
                      closable
                      onClose={() => handleRemoveSize(size)}
                      className="font-barlow bg-zinc-700 border-zinc-600 text-white px-3 py-1"
                    >
                      {size}
                    </Tag>
                  ))}
                </div>
                {sizes.length === 0 && (
                  <p className="font-barlow text-red-400 text-xs mt-2">At least one size is required</p>
                )}
              </div>

              {/* Generate Variants Button */}
              {sizes.length > 0 && (
                <div className="mb-4">
                  <Button
                    onClick={generateAllVariants}
                    className="btn-yellow-principal font-android uppercase tracking-wider"
                    style={{ backgroundColor: '#E3AF25', borderColor: '#E3AF25', color: '#000000' }}
                  >
                    Generate All Variants ({sizes.length} {sizes.length === 1 ? 'size' : 'sizes'})
                  </Button>
                </div>
              )}

              {/* Variants Table */}
              {variants.length > 0 && (
                <div className="mt-4">
                  <label className="font-android text-white text-xs uppercase tracking-wider block mb-2">
                    VARIANT PRICES & STOCK
                  </label>
                  <Table
                    dataSource={variants.map((v, i) => ({ ...v, key: i }))}
                    pagination={false}
                    size="small"
                    className="variants-table"
                    columns={[
                      {
                        title: <span className="font-android text-white text-xs uppercase">SKU</span>,
                        dataIndex: 'sku',
                        key: 'sku',
                        render: (value: string, _: any, index: number) => (
                          <Input
                            value={value}
                            onChange={(e) => handleVariantChange(index, 'sku', e.target.value)}
                            placeholder="SKU"
                            className="font-barlow !bg-zinc-700 !border-none !text-white !rounded-none"
                            size="small"
                          />
                        ),
                      },
                      {
                        title: <span className="font-android text-white text-xs uppercase">Size</span>,
                        dataIndex: 'size',
                        key: 'size',
                        render: (text, _, index) => (
                          <Select
                            value={text}
                            onChange={(value) => handleVariantChange(index, 'size', value)}
                            className="w-full font-barlow"
                            size="small"
                          >
                            {sizes.map(size => (
                              <Option key={size} value={size}>{size}</Option>
                            ))}
                          </Select>
                        ),
                      },
                      {
                        title: <span className="font-android text-white text-xs uppercase">Price ($)</span>,
                        dataIndex: 'price',
                        key: 'price',
                        render: (value: number, _: any, index: number) => (
                          <InputNumber
                            value={value}
                            onChange={(val) => handleVariantChange(index, 'price', val || 0)}
                            min={0}
                            step={0.01}
                            precision={2}
                            className="w-full font-barlow"
                            size="small"
                          />
                        ),
                      },
                      {
                        title: <span className="font-android text-white text-xs uppercase">Stock</span>,
                        dataIndex: 'stockQuantity',
                        key: 'stockQuantity',
                        render: (value: number, _: any, index: number) => (
                          <InputNumber
                            value={value}
                            onChange={(val) => handleVariantChange(index, 'stockQuantity', val || 0)}
                            min={0}
                            step={1}
                            precision={0}
                            className="w-full font-barlow"
                            size="small"
                          />
                        ),
                      },
                      {
                        title: '',
                        key: 'actions',
                        width: 50,
                        render: (_: any, __: any, index: number) => (
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleRemoveVariant(index)}
                            size="small"
                            className="!text-white [&_svg]:!text-white"
                          />
                        ),
                      },
                    ]}
                  />
                  <Button
                    icon={<PlusOutlined />}
                    onClick={handleAddVariant}
                    className="mt-2 !bg-zinc-700 font-android !border-zinc-600 !text-white hover:!bg-zinc-600 uppercase tracking-wider"
                    size="small"
                  >
                    Add Variant
                  </Button>
                </div>
              )}
              
              {/* Show message if no variants generated */}
              {sizes.length > 0 && variants.length === 0 && (
                <div className="mt-4 p-3 border-2 border-yellow-principal/50 bg-yellow-principal/10">
                  <p className="font-barlow text-yellow-principal text-sm">
                    Click "Generate All Variants" to create price entries for each size.
                  </p>
                </div>
              )}
            </FormSection>

            {/* Media Section */}
            <FormSection title="Product Images" icon={<PictureOutlined />}>
              <Upload.Dragger
                beforeUpload={() => false}
                onChange={handleImagesChange}
                multiple
                maxCount={5}
                accept="image/*"
                fileList={imageFiles.map((file, index) => ({
                  uid: `${index}`,
                  name: file.name,
                  status: 'done' as const,
                  originFileObj: file as any
                }))}
                className="!bg-zinc-800/50 !border-zinc-600 !border-dashed hover:!border-yellow-principal transition-colors"
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined className="!text-yellow-principal text-4xl" />
                </p>
                <p className="font-android text-white uppercase tracking-wider text-sm">
                  Click or drag images here
                </p>
                <p className="font-barlow text-gray-400 text-xs mt-1">
                  Up to 5 images, max 5MB each (JPG, PNG, GIF)
                </p>
              </Upload.Dragger>
              
              {imagePreviews.length > 0 && (
                <div className="mt-4">
                  <p className="font-android text-white text-xs mb-3 uppercase tracking-wider">Preview ({imagePreviews.length}/5):</p>
                  <div className="grid grid-cols-5 gap-3">
                    {imagePreviews.map((preview, index) => (
                      <div 
                        key={index}
                        className="relative aspect-square overflow-hidden border-2 border-zinc-600 hover:border-yellow-principal transition-colors"
                      >
                        <img 
                          src={preview} 
                          alt={`Preview ${index + 1}`} 
                          className="w-full h-full object-cover"
                        />
                        {/* Gradient overlay */}
                        <div 
                          className="absolute inset-0 pointer-events-none"
                          style={{
                            background: 'linear-gradient(to top, rgba(227, 175, 37, 0.3) 0%, transparent 50%)'
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Form.Item
                label={<span className="font-android text-white text-xs uppercase tracking-wider mt-4 block">Video URL (Optional)</span>}
                name="videoUrl"
                className="mb-0 mt-4"
              >
                <Input 
                  className="!bg-zinc-800 font-barlow !border-none !text-white !rounded-none" 
                  placeholder="e.g., https://www.youtube.com/embed/..."
                />
              </Form.Item>
            </FormSection>

            {/* Documents Section */}
            <FormSection title="Technical Documents" icon={<FilePdfOutlined />}>
              <div className="space-y-3">
                <Button 
                  onClick={handleDocumentAdd}
                  icon={<UploadOutlined />}
                  className="!bg-zinc-800 font-android !border-zinc-600 !text-white hover:!bg-zinc-700 hover:!border-yellow-principal hover:!text-yellow-principal uppercase tracking-wider"
                  disabled={documentFiles.length >= 3}
                >
                  <span className="!text-white">Add PDF Document ({documentFiles.length}/3)</span>
                </Button>

                {documentFiles.map((doc, index) => (
                  <div 
                    key={index} 
                    className="bg-zinc-800/50 p-4 border border-zinc-600"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2">
                        <FilePdfOutlined className="text-yellow-principal text-lg" />
                        <span className="font-barlow text-white text-sm">
                          {doc.existingDocId ? `${doc.name}.pdf` : doc.file.name}
                          {doc.existingDocId && (
                            <span className="ml-2 text-xs text-yellow-principal">(Existing)</span>
                          )}
                        </span>
                      </div>
                      <Button
                        type="text"
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={() => handleDocumentRemove(index)}
                        className="!text-red-500 hover:!text-red-400"
                      />
                    </div>
                    <Input
                      placeholder="Document Name"
                      value={doc.name}
                      onChange={(e) => handleDocumentMetadataChange(index, 'name', e.target.value)}
                      className="!bg-zinc-900 font-barlow !border-none !text-white mb-2 !rounded-none"
                    />
                    <TextArea
                      rows={2}
                      placeholder="Document Description (Optional)"
                      value={doc.description}
                      onChange={(e) => handleDocumentMetadataChange(index, 'description', e.target.value)}
                      className="!bg-zinc-900 font-barlow !border-none !text-white !rounded-none"
                    />
                  </div>
                ))}

                {documentFiles.length === 0 && (
                  <p className="font-barlow text-gray-500 text-sm">
                    No documents added. Click the button above to add PDFs.
                  </p>
                )}
              </div>
            </FormSection>

            {/* Footer Actions */}
            <div className="flex justify-end gap-4 pt-6 border-t border-zinc-700">
              <Button
                onClick={handleCancel}
                className="font-android !bg-transparent !border-zinc-600 !text-white hover:!border-yellow-principal hover:!text-yellow-principal uppercase tracking-wider h-11 px-6"
              >
                <span className="!text-white">Cancel</span>
              </Button>
              <PrimaryButton
                htmlType="submit"
                loading={addProduct.isPending || updateProduct.isPending}
                className="h-11 px-8"
              >
                {isEditing ? 'UPDATE PRODUCT' : 'CREATE PRODUCT'}
              </PrimaryButton>
            </div>
          </Form>
        </div>
      </div>
    </Modal>
  );
};
